import { useEffect, useState, useCallback } from "react";
import { CheckSquare, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STAGE = { SUPERVISOR: "Supervisor", HR: "HR", SECRETARIAT: "Final · Sekretariat" };

export default function Approvals() {
  const [queue, setQueue] = useState([]);
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    api.get("/forms/approvals").then((r) => setQueue(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (id, action) => {
    try {
      await api.post(`/forms/${id}/review`, { action, note: notes[id] || "" });
      toast.success(action === "APPROVE" ? "Formulir disetujui" : "Formulir ditolak");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Persetujuan</h1>
        <p className="mt-1 text-sm text-slate-500">Alur persetujuan: Supervisor → HR → Final (Sekretariat). Tinjau permohonan yang ditugaskan kepada Anda.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {queue.length === 0 && (
          <Card className="col-span-full flex flex-col items-center gap-2 rounded-2xl border-dashed p-10 text-center text-slate-400">
            <CheckSquare className="h-8 w-8" />
            <p>Tidak ada permohonan yang menunggu persetujuan.</p>
          </Card>
        )}
        {queue.map((form) => (
          <Card key={form.id} data-testid="approval-card" className="rounded-2xl border-slate-200 p-5 dark:border-slate-800">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-slate-500">{form.employee_name}</p>
                <p className="font-semibold">{form.title}</p>
                <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">{form.form_type_label}</span>
              </div>
              <div className="text-right">
                <span className="block font-mono text-[11px] text-slate-400">{form.form_number}</span>
                <span data-testid="approval-stage-badge" className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">Tahap: {STAGE[form.current_stage] || form.current_stage}</span>
              </div>
            </div>
            {form.description && <p className="mt-2 text-sm text-slate-500">{form.description}</p>}
            {(form.start_date || form.end_date) && (
              <p className="mt-2 text-xs text-slate-400">Periode: {fmtDate(form.start_date)} — {fmtDate(form.end_date)}</p>
            )}
            <Input className="mt-3" placeholder="Catatan (opsional)" data-testid="approval-note-input"
              value={notes[form.id] || ""} onChange={(e) => setNotes({ ...notes, [form.id]: e.target.value })} />
            <div className="mt-3 flex gap-2">
              <Button data-testid="approval-approve-button" size="sm" onClick={() => review(form.id, "APPROVE")} className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700">
                <Check className="h-4 w-4" /> Setujui
              </Button>
              <Button data-testid="approval-reject-button" size="sm" variant="destructive" onClick={() => review(form.id, "REJECT")} className="flex-1 gap-1">
                <X className="h-4 w-4" /> Tolak
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
